import { browserApi, type Api } from "./http";

/** Metadata comes from the scoped record. Durable detail still comes from the stream fold. */
export interface RunRecord {
  run_id: string;
  /** The incident label and the server's capability for this caller (#1057). */
  incident?: (Incident & { may_change?: boolean }) | null;
  title: string | null;
  fingerprint: string | null;
  status: string;
  dispatched_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  account_id: string | null;
  last_seq: number;
  /** The incident's tickets in the workspace's queue (#265). Absent from a record read before it. */
  tickets?: RunTicket[];
  /** The messages its Slack connections posted (#818). Absent from a record read before it. */
  slack_posts?: SlackPost[];
  /** What the ledger debited for it, in credits (#1373). Absent from a record read before it. */
  cost?: RunCost;
  /** The terminal's own report of what it spent (`store/log.py::token_usage`); null until then. */
  token_usage?: { totals?: TokenTotals | null } | null;
}

/** The run's own debit (null until its terminal writes one) and its chat turns' debits. */
export interface RunCost {
  credits: number | null;
  chat_turn_credits: number;
  chat_turns: number;
}

/** The grand totals the runtime's budget guard reports beside its per-tier counts. */
export interface TokenTotals {
  total_input_tokens?: number;
  total_output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_write_input_tokens?: number;
}

/** One Slack message a delivery of the run posted: where it is, and which event it carried. */
export interface SlackPost {
  channel: string;
  ts: string;
  /** The event in words — "conclusion", "approval request" — or null for one nobody has named. */
  event: string | null;
}

/** One ticket the incident has: which queue, its key, and where to open it, if linkable. */
export interface RunTicket {
  kind: string;
  key: string;
  url: string | null;
}

/** The tickets out of an answer's `tickets`, dropping any row without a queue and a key. */
export function ticketsFrom(value: unknown): RunTicket[] {
  if (!Array.isArray(value)) return [];
  const tickets: RunTicket[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const row = item as Record<string, unknown>;
    if (typeof row.kind !== "string" || typeof row.key !== "string" || row.key === "") continue;
    tickets.push({ kind: row.kind, key: row.key, url: typeof row.url === "string" ? row.url : null });
  }
  return tickets;
}

export function readRunRecord(
  runId: string,
  api: Api = browserApi,
): Promise<RunRecord> {
  return api.get<RunRecord>(`/api/v1/runs/${encodeURIComponent(runId)}`);
}

/** Public response of resolve/reopen; the write does not return a capability. */
export interface Incident {
  id: string;
  status: string;
  resolved_at: string | null;
}

export function changeIncident(incident: Incident, api: Api = browserApi): Promise<Incident> {
  const action = incident.status === "closed" ? "reopen" : "resolve";
  return api.post<Incident>(`/api/v1/incidents/${encodeURIComponent(incident.id)}/${action}`, {
    seen_status: incident.status,
  });
}
